import naja from 'naja';
import { getCookie, setSessionCookie as setSessionCookieRaw } from './manage-cookies.mts';

// --- Configuration ---
const STORAGE_KEY = 'lsf-filter';
const CLEARED_KEY = 'lsf-filter-cleared';
const SESSION_COOKIE = 'lsf-filter-session';
const THRESHOLD_MS = 2 * 60 * 60 * 1000; // 2 hours

// --- Types ---
interface FilterState {
	url: string;
	timestamp: number;
}

// --- Cookie helpers ---
function hasSessionCookie(): boolean {
	return getCookie(SESSION_COOKIE) !== undefined;
}

function renewSessionCookie(): void {
	setSessionCookieRaw(SESSION_COOKIE, '1');
}

// --- localStorage helpers ---
function getFilterState(): FilterState | null {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		return raw ? JSON.parse(raw) as FilterState : null;
	}
	catch {
		return null;
	}
}

function setFilterState(url: string): void {
	localStorage.setItem(STORAGE_KEY, JSON.stringify({
		url,
		timestamp: Date.now(),
	} satisfies FilterState));
}

function getClearedUrl(): string | null {
	return localStorage.getItem(CLEARED_KEY);
}

function setClearedUrl(url: string): void {
	localStorage.setItem(CLEARED_KEY, url);
}

function clearClearedUrl(): void {
	localStorage.removeItem(CLEARED_KEY);
}

// --- URL helpers ---
const FILTER_PREFIXES = ['courseFilter-', 'showAllCourses'];

function hasFilterParams(url: URL): boolean {
	return [...url.searchParams.keys()].some(key =>
		FILTER_PREFIXES.some(prefix => key.startsWith(prefix)),
	);
}

function cleanFilterUrl(url: URL): string {
	const clean = new URL(url.href);
	[...clean.searchParams.keys()]
		.filter(key => FILTER_PREFIXES.some(prefix => key.startsWith(prefix)))
		.forEach(key => clean.searchParams.delete(key));

	const search = clean.search;
	return clean.pathname + (search === '?' ? '' : search);
}

function urlFingerprint(url: URL): string {
	return url.pathname + url.search;
}

// --- Restore bar ---
function showRestoreBar(url: string): void {
	const container = document.getElementById('course-filter-restore');
	if (!container) {
		return;
	}

	const link = container.querySelector<HTMLAnchorElement>('[data-restore-link]');
	if (link) {
		link.href = url;
		link.addEventListener('click', () => clearClearedUrl());
	}

	const dismissBtn = container.querySelector('[data-restore-dismiss]');
	if (dismissBtn) {
		dismissBtn.addEventListener('click', () => {
			container.hidden = true;
			clearClearedUrl();
		});
	}

	container.hidden = false;
}

// --- Main logic (runs on full page load) ---
const currentUrl = new URL(window.location.href);
const currentFingerprint = urlFingerprint(currentUrl);
const saved = getFilterState();
const sessionActive = hasSessionCookie();

if (hasFilterParams(currentUrl)) {
	// Page loaded with filter params — was this a browser restore?
	clearClearedUrl(); // clear any pending restore notification

	const isBrowserRestore = saved !== null
		&& saved.url === currentFingerprint
		&& !sessionActive
		&& (Date.now() - saved.timestamp) > THRESHOLD_MS;

	if (isBrowserRestore) {
		// Save URL for the restore button, set session, redirect to clean page
		setClearedUrl(currentFingerprint);
		renewSessionCookie();
		window.location.replace(cleanFilterUrl(currentUrl));
		// Script stops here — navigation away
	}
	else {
		// Shared link or same-session navigation — apply filters normally
		setFilterState(currentFingerprint);
		renewSessionCookie();
	}
}
else {
	// Clean page (no filter params)
	renewSessionCookie();

	const clearedUrl = getClearedUrl();
	if (clearedUrl) {
		showRestoreBar(clearedUrl);
	}
}

// --- Naja hook: keep localStorage in sync on AJAX filter changes ---
naja.addEventListener('complete', () => {
	const url = new URL(window.location.href);
	setFilterState(urlFingerprint(url));
	renewSessionCookie();
});
